<script setup lang="ts">
    import BaseIconButton from '@/components/BaseIconButton.vue';
    import BaseInput from '@/components/BaseInput.vue';
    import BaseToggleButton from '@/components/BaseToggleButton.vue';
    import ButtonColorPicker from '@/components/ButtonColorPicker.vue';
    import FontSelector from '@/components/FontSelector.vue';
    import BaseTextAlinement from '@/components/BaseTextAlinement.vue';
    import IconCharacterSpacing from '@/components/icons/IconCharacterSpacing.vue';
    import IconLineSpacing from '@/components/icons/IconLineSpacing.vue';
    import CharacterSpacingModal from '@/components/CharacterSpacingModal.vue';
    import { useTextField } from '@/composables/useTextField';
    import { useConfigStore } from '@/store/config.store';
    import { dataStore } from '@/commons/DataStore';
    import { toRef } from 'vue';
    import { storeToRefs } from 'pinia';
    import type { ITextboxField } from '@/typescript/interfaces/ITextboxField';

    const { field } = defineProps<{ field: ITextboxField }>();
    const { currentSide, isColorsLocked } = storeToRefs(useConfigStore());
    const fabricWorld = dataStore.getFabricWorld(currentSide.value);

    const fieldRef = toRef(() => field);
    const { angle, color, font, fontSize, isBold, isItalic, isUnderlined } = useTextField(fieldRef, fabricWorld);
</script>

<template>
    <div class="flex h-full min-w-0 flex-row items-center justify-around gap-2 bg-white py-4">
        <BaseInput
            v-model="fontSize"
            name="font_size"
            :label="$t('font_size')"
            :show-label="false"
            class="h-8 w-16 flex-none rounded-none border border-secondary"
        >
            <div class="flex h-full flex-col bg-transparent">
                <BaseIconButton
                    icon-name="ChevronUpIcon"
                    :label="$t('increase_font_size')"
                    class="h-2.5"
                    @click="fontSize++"
                />
                <BaseIconButton
                    icon-name="ChevronDownIcon"
                    class="h-2.5"
                    :label="$t('decrease_font_size')"
                    @click="fontSize--"
                />
            </div>
        </BaseInput>
        <FontSelector v-model="font" />
        <BaseToggleButton v-model="isBold">
            <span class="sr-only"> {{ $t('bold') }} </span>
            <span class="flex h-full w-full items-center justify-center font-serif text-xl font-bold"> {{ $t('bold_symbol') }} </span>
        </BaseToggleButton>
        <BaseToggleButton v-model="isItalic">
            <span class="sr-only"> {{ $t('italic') }} </span>
            <span class="flex h-full w-full items-center justify-center font-serif text-xl italic"> {{ $t('italic_symbol') }} </span>
        </BaseToggleButton>
        <BaseToggleButton v-model="isUnderlined">
            <span class="sr-only"> {{ $t('underline') }} </span>
            <span class="flex h-full w-full items-center justify-center font-serif text-xl underline">
                {{ $t('underline_symbol') }}
            </span>
        </BaseToggleButton>
        <BaseInput
            v-model="angle"
            name="angle"
            :show-label="false"
            :label="$t('angle')"
            class="h-8 w-14 flex-none rounded-none border border-border-primary"
        >
            <div class="flex h-full flex-col bg-transparent">
                <BaseIconButton
                    icon-name="ChevronUpIcon"
                    :label="$t('increase_angle')"
                    class="h-2.5"
                    @click="angle++"
                />
                <BaseIconButton
                    icon-name="ChevronDownIcon"
                    class="h-2.5"
                    :label="$t('decrease_angle')"
                    @click="angle--"
                />
            </div>
            <span class="absolute -top-1 right-3 h-2">°</span>
        </BaseInput>
        <ButtonColorPicker
            v-if="!isColorsLocked"
            v-model="color"
            :field
        />
        <CharacterSpacingModal
            :title="$t('character_spacing')"
            :field
            store-update-key="characterSpacing"
            spacing-type="characterSpacing"
            min="0"
            max="50"
        >
            <IconCharacterSpacing />
        </CharacterSpacingModal>

        <CharacterSpacingModal
            :title="$t('line_spacing')"
            :field
            store-update-key="lineSpacing"
            spacing-type="lineSpacing"
            min="-16"
            max="50"
        >
            <IconLineSpacing />
        </CharacterSpacingModal>
        <BaseTextAlinement :field />
    </div>
</template>
